import { useDispatch, useSelector } from 'react-redux';
import './css/app.css'
import './css/common.css'
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from "react-router-dom"
import { change_Current_Location, changeHeaderTitle } from '../store';
import { RefreshCw, Wallet, ArrowDownCircle, ArrowUpCircle, DollarSign } from 'lucide-react';

import axios from 'axios';
import { changeNum } from './common/function';
import useWindowSize from './hook/useWindowSize';

function AgentDashboard() {
    let dispatch = useDispatch()
    let navigate = useNavigate()
    let MyData = useSelector((state) => { return state.MyData })
    const { Rwidth } = useWindowSize();
    const isMobile = Rwidth <= 767;

    const [walletBalance, setWalletBalance] = useState(null);

    const [withdrawals, setWithdrawals] = useState([]);
    const [pendingDeposits, setPendingDeposits] = useState([]);
    const [withdrawalsPage, setWithdrawalsPage] = useState(1);
    const [pendingDepositsPage, setPendingDepositsPage] = useState(1);
    const [activeTab, setActiveTab] = useState('withdrawals');
    const [refreshing, setRefreshing] = useState(false);
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

    useEffect(() => {
        dispatch(change_Current_Location("지갑"))
        dispatch(changeHeaderTitle({
            mainTitle: "지갑",
            subTitle: ""
        }))
        
        loadWalletData();
        // 초기 로드 시 입금 내역도 함께 로드
        loadPendingDeposits();
        
        // 10초마다 자동 새로고침
        const interval = setInterval(() => {
            loadWalletData();
            // 새로고침 시에도 입금 내역 업데이트
            if (activeTab === 'pending') {
                loadPendingDeposits();
            }
        }, 10000);

        return () => clearInterval(interval);
    }, [])

    // 탭 변경 시 해당 데이터 로드
    useEffect(() => {
        if (activeTab === 'withdrawals') {
            loadWithdrawals();
        } else {
            loadPendingDeposits();
        }
    }, [activeTab, withdrawalsPage, pendingDepositsPage])

    // 지갑 데이터 로드
    const loadWalletData = async () => {
        try {
            setRefreshing(true);
            
            // 지갑 잔액 조회
            await loadWalletBalance();

            // 현재 탭에 따라 내역 조회
            if (activeTab === 'withdrawals') {
                await loadWithdrawals();
            } else {
                await loadPendingDeposits();
            }
        } catch (error) {
            console.error('지갑 데이터 로드 오류:', error);
        } finally {
            setRefreshing(false);
        }
    }

    // 지갑 잔액 조회
    const loadWalletBalance = async () => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API + "/api/agent-wallet/balance",
                { withCredentials: true }
            );
            
            if (response?.data?.code < 2000) {
                const raw = response.data.data || response.data;
                // balance가 중첩 객체인 경우 플랫하게 정규화
                const bal = raw.balance && typeof raw.balance === 'object' ? raw.balance : {};
                const normalized = {
                    ...raw,
                    available_balance: bal.available_balance ?? raw.available_balance ?? raw.availableBalance ?? 0,
                    current_balance: bal.total_balance ?? raw.current_balance ?? raw.currentBalance ?? 0,
                    frozen_balance: bal.locked_balance ?? raw.frozen_balance ?? raw.frozenBalance ?? raw.locked_balance ?? raw.lockedBalance ?? 0,
                    locked_balance: bal.locked_balance ?? raw.locked_balance ?? raw.lockedBalance ?? 0,
                    pending_deposit_amount: bal.pending_deposit ?? raw.pending_deposit_amount ?? raw.pendingDepositAmount ?? 0,
                    withdrawn_amount: bal.total_withdraw ?? raw.withdrawn_amount ?? raw.withdrawnAmount ?? 0,
                    // 계좌 정보: account 객체가 없으면 최상위 필드에서 구성
                    account: raw.account || {
                        bank_name: raw.bank_nm || raw.bank_name || null,
                        bank_nm: raw.bank_nm || null,
                        account_number: raw.accnt_no || raw.account_number || null,
                        accnt_no: raw.accnt_no || null,
                        account_holder: raw.accnt_nm || raw.account_holder || null,
                        accnt_holder: raw.accnt_nm || null,
                    },
                };
                setWalletBalance(normalized);
            } else if (response?.data?.code === 9999) {
                navigate('/');
            }
        } catch (error) {
            console.error('잔액 조회 오류:', error);
        }
    }


    // 출금 내역 조회
    const loadWithdrawals = async () => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API + "/api/agent-wallet/withdrawals",
                {
                    withCredentials: true,
                    params: { 
                        page: withdrawalsPage, 
                        limit: 10,
                        status: 'P' // 기본값: 완료된 출금만
                    }
                }
            );
            
            if (response?.data?.code < 2000) {
                const data = response.data.data || response.data;
                setWithdrawals(data.withdrawals || data.list || []);
            } else if (response?.data?.code === 9999) {
                navigate('/');
            }
        } catch (error) {
            console.error('출금 내역 조회 오류:', error);
        }
    }

    // 입금 내역 조회
    const loadPendingDeposits = async () => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API + "/api/agent-wallet/deposits",
                {
                    withCredentials: true,
                    params: {
                        page: pendingDepositsPage,
                        limit: 10
                    }
                }
            );

            if (response?.data?.code < 2000) {
                const data = response.data.data || response.data;
                setPendingDeposits(data.deposits || data.list || []);
            } else if (response?.data?.code === 9999) {
                navigate('/');
            }
        } catch (error) {
            console.error('입금 내역 조회 오류:', error);
        }
    }

    // 출금 요청
    const requestWithdraw = async (amount) => {
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + "/api/agent-wallet/withdraw",
                { amount },
                { withCredentials: true }
            );
            
            if (response?.data?.code < 2000) {
                alert(response.data.msg || '출금 요청이 완료되었습니다.');
                // 출금 후 데이터 새로고침
                await loadWalletData();
                setWithdrawModalOpen(false);
            } else if (response?.data?.code === 9999) {
                navigate('/');
            } else {
                alert(response.data.msg || '출금 요청에 실패했습니다.');
            }
        } catch (error) {
            console.error('출금 요청 오류:', error);
            alert('출금 요청 중 오류가 발생했습니다.');
        }
    }

    // 출금 모달 관련 함수
    const parseKRW = (s = "") => {
        if (typeof s === "number") return s;
        const digits = String(s).replace(/[^\d]/g, "");
        return digits ? parseInt(digits, 10) : 0;
    };

    return (
        <>
            <div className="flex overflow-hidden">
                {withdrawModalOpen && (
                    <WithdrawModal
                        currentBalance={Number(walletBalance?.available_balance ?? 0)}
                        withdrawable={Number(walletBalance?.available_balance ?? 0)}
                        onClose={() => setWithdrawModalOpen(false)}
                        onWithdraw={requestWithdraw}
                        parseKRW={parseKRW}
                        withdrawalFee={Number(walletBalance?.withdrawal_fee ?? 0)}
                    />
                )}
                <div className="content" id="contents" style={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                <div className="inner-wrap dashboard" style={{ width: "100%", maxWidth: "100%" }}>
                    <div className="content-container c-left sales" style={{ width: "100%", maxWidth: "100%" }}>
                        <div className="content-wrap" style={{ paddingBottom: "40px", width: "100%", maxWidth: "100%", padding: "10px" }}>
                            
                            {/* 지갑 메인 카드 */}
                            <div className="content-item" style={{ marginBottom: "24px" }}>
                                <div style={{ 
                                    background: "linear-gradient(135deg, #16639C 0%, #0f4c75 100%)",
                                    borderRadius: "16px",
                                    padding: isMobile ? "20px" : "32px",
                                    color: "#fff",
                                    boxShadow: "0 10px 30px rgba(22, 99, 156, 0.2)",
                                    position: "relative",
                                    overflow: "hidden"
                                }}>
                                    {/* 배경 장식 */}
                                    <div style={{
                                        position: "absolute",
                                        top: "-50px",
                                        right: "-50px",
                                        width: "200px",
                                        height: "200px",
                                        background: "rgba(255, 255, 255, 0.1)",
                                        borderRadius: "50%",
                                        filter: "blur(40px)"
                                    }} />
                                    <div style={{
                                        position: "absolute",
                                        bottom: "-30px",
                                        left: "-30px",
                                        width: "150px",
                                        height: "150px",
                                        background: "rgba(255, 255, 255, 0.08)",
                                        borderRadius: "50%",
                                        filter: "blur(30px)"
                                    }} />
                                    
                                    {/* 헤더 */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: "center", 
                                        justifyContent: "space-between", 
                                        marginBottom: "24px",
                                        position: "relative",
                                        zIndex: 1
                                    }}>
                                        <div style={{ display: 'flex', alignItems: "center", gap: "12px" }}>
                                            <div style={{
                                                width: isMobile ? "44px" : "52px",
                                                height: isMobile ? "44px" : "52px",
                                                borderRadius: "12px",
                                                background: "rgba(255, 255, 255, 0.2)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backdropFilter: "blur(10px)"
                                            }}>
                                                <Wallet size={isMobile ? 24 : 28} style={{ color: "#fff" }} />
                                            </div>
                                            <div>
                                                <h2 style={{ 
                                                    margin: 0, 
                                                    fontSize: isMobile ? "18px" : "24px", 
                                                    fontWeight: "700",
                                                    color: "#fff"
                                                }}>
                                                    내 지갑
                                                </h2>
                                                <p style={{ 
                                                    margin: "4px 0 0 0", 
                                                    fontSize: isMobile ? "12px" : "14px", 
                                                    color: "rgba(255, 255, 255, 0.8)",
                                                    fontWeight: "400"
                                                }}>
                                                    {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <button 
                                                onClick={() => setWithdrawModalOpen(true)}
                                                style={{
                                                    padding: isMobile ? "8px 12px" : "10px 16px",
                                                    background: "rgba(255, 255, 255, 0.95)",
                                                    color: "#16639C",
                                                    border: "none",
                                                    borderRadius: "10px",
                                                    fontSize: isMobile ? "13px" : "14px",
                                                    fontWeight: "700",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "6px",
                                                    transition: "all 0.2s",
                                                    whiteSpace: "nowrap"
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = "#fff";
                                                    e.target.style.transform = "translateY(-1px)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = "rgba(255, 255, 255, 0.95)";
                                                    e.target.style.transform = "translateY(0)";
                                                }}
                                            >
                                                <ArrowUpCircle size={16} />
                                                출금
                                            </button>
                                            <button 
                                                onClick={loadWalletData}
                                                disabled={refreshing}
                                                style={{
                                                    padding: "10px",
                                                    background: "rgba(255, 255, 255, 0.2)",
                                                    border: "none",
                                                    borderRadius: "10px",
                                                    cursor: refreshing ? "not-allowed" : "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: isMobile ? "40px" : "44px",
                                                    height: isMobile ? "40px" : "44px",
                                                    color: "#fff",
                                                    transition: "all 0.2s",
                                                    backdropFilter: "blur(10px)"
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!refreshing) {
                                                        e.target.style.background = "rgba(255, 255, 255, 0.3)";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!refreshing) {
                                                        e.target.style.background = "rgba(255, 255, 255, 0.2)";
                                                    }
                                                }}
                                                aria-label="새로고침"
                                            >
                                                <RefreshCw 
                                                    size={20} 
                                                    style={{ 
                                                        animation: refreshing ? "spin 1s linear infinite" : "none"
                                                    }} 
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* 사용가능잔액 */}
                                    <div style={{ position: "relative", zIndex: 1 }}>
                                        <p style={{ 
                                            margin: "0 0 8px 0", 
                                            fontSize: isMobile ? "13px" : "15px", 
                                            color: "rgba(255, 255, 255, 0.9)",
                                            fontWeight: "500"
                                        }}>
                                            사용가능잔액
                                        </p>
                                        <div style={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                                            <h1 style={{ 
                                                margin: 0, 
                                                fontSize: isMobile ? "32px" : "48px", 
                                                fontWeight: "800",
                                                color: "#fff",
                                                lineHeight: "1.2"
                                            }}>
                                                {changeNum(Number(walletBalance?.available_balance ?? walletBalance?.availableBalance ?? walletBalance?.current_balance ?? walletBalance?.currentBalance ?? 0))}
                                            </h1>
                                            <span style={{ 
                                                fontSize: isMobile ? "20px" : "28px", 
                                                color: "rgba(255, 255, 255, 0.9)",
                                                fontWeight: "600"
                                            }}>
                                                원
                                            </span>
                                        </div>
                                        {(Number(walletBalance?.frozen_balance ?? walletBalance?.frozenBalance ?? walletBalance?.locked_balance ?? walletBalance?.lockedBalance ?? 0) > 0) && (
                                            <p style={{ 
                                                margin: "8px 0 0 0", 
                                                fontSize: isMobile ? "12px" : "13px", 
                                                color: "rgba(255, 255, 255, 0.7)",
                                                fontWeight: "400"
                                            }}>
                                                동결잔액: {changeNum(Number(walletBalance?.frozen_balance ?? walletBalance?.frozenBalance ?? walletBalance?.locked_balance ?? walletBalance?.lockedBalance ?? 0))}원
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                                {/* 출금 계좌 정보 */}
                                {walletBalance?.account && (
                                    <div style={{
                                        background: "#f8fafc",
                                        borderRadius: "10px",
                                        padding: isMobile ? "10px 14px" : "12px 20px",
                                        border: "1px solid #e2e8f0",
                                        marginTop: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: isMobile ? "6px" : "8px",
                                        flexWrap: "wrap"
                                    }}>
                                        <DollarSign size={14} style={{ color: "#64748b", flexShrink: 0 }} />
                                        <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#64748b" }}>출금계좌</span>
                                        <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#1e293b", fontWeight: "600" }}>
                                            {walletBalance.account.bank_name || walletBalance.account.bank_nm || '-'}
                                        </span>
                                        <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#1e293b", fontWeight: "600", fontFamily: "monospace" }}>
                                            {walletBalance.account.account_number || walletBalance.account.accnt_no || '-'}
                                        </span>
                                        <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#64748b" }}>
                                            ({walletBalance.account.account_holder || walletBalance.account.accnt_holder || '-'})
                                        </span>
                                    </div>
                                )}

                            {/* 탭 메뉴 */}
                            <div className="content-item" style={{ marginTop: "24px" }}>
                                <div style={{ 
                                    background: "#fff",
                                    borderRadius: "16px",
                                    padding: isMobile ? "16px" : "24px",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                    border: "1px solid #f1f5f9"
                                }}>
                                    <div style={{ 
                                        display: "flex", 
                                        gap: "8px", 
                                        background: "#f8fafc",
                                        borderRadius: "12px",
                                        padding: "4px",
                                        marginBottom: "20px"
                                    }}>
                                        <button
                                            onClick={() => {
                                                setActiveTab('withdrawals');
                                                setWithdrawalsPage(1);
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: isMobile ? "10px 16px" : "12px 24px",
                                                background: activeTab === 'withdrawals' ? "#fff" : "transparent",
                                                border: "none",
                                                borderRadius: "8px",
                                                color: activeTab === 'withdrawals' ? "#16639C" : "#64748b",
                                                fontWeight: activeTab === 'withdrawals' ? "700" : "500",
                                                cursor: "pointer",
                                                fontSize: isMobile ? "13px" : "15px",
                                                transition: "all 0.2s",
                                                whiteSpace: "nowrap",
                                                boxShadow: activeTab === 'withdrawals' ? "0 2px 4px rgba(0, 0, 0, 0.05)" : "none"
                                            }}
                                        >
                                            출금 내역
                                        </button>
                                        <button
                                            onClick={() => {
                                                setActiveTab('pending');
                                                setPendingDepositsPage(1);
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: isMobile ? "10px 16px" : "12px 24px",
                                                background: activeTab === 'pending' ? "#fff" : "transparent",
                                                border: "none",
                                                borderRadius: "8px",
                                                color: activeTab === 'pending' ? "#16639C" : "#64748b",
                                                fontWeight: activeTab === 'pending' ? "700" : "500",
                                                cursor: "pointer",
                                                fontSize: isMobile ? "13px" : "15px",
                                                transition: "all 0.2s",
                                                whiteSpace: "nowrap",
                                                boxShadow: activeTab === 'pending' ? "0 2px 4px rgba(0, 0, 0, 0.05)" : "none"
                                            }}
                                        >
                                            입금 내역
                                        </button>
                                    </div>
                                    
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                        {activeTab === 'withdrawals' ? (
                                            withdrawals.length === 0 ? (
                                                <div style={{ padding: "60px 20px", textAlign: "center", color: "#94a3b8", fontSize: "14px" }}>
                                                    출금 내역이 없습니다.
                                                </div>
                                            ) : (
                                                withdrawals.map((data, i) => {
                                                    const status = data.transfer_status || data.status;
                                                    const statusMap = {
                                                        'P': { text: '완료', color: '#059669', bg: '#f0fdf4' },
                                                        'SUCCESS': { text: '완료', color: '#059669', bg: '#f0fdf4' },
                                                        '완료': { text: '완료', color: '#059669', bg: '#f0fdf4' },
                                                        'W': { text: '대기', color: '#d97706', bg: '#fffbeb' },
                                                        'PENDING': { text: '대기', color: '#d97706', bg: '#fffbeb' },
                                                        '대기': { text: '대기', color: '#d97706', bg: '#fffbeb' },
                                                        'F': { text: '실패', color: '#dc2626', bg: '#fef2f2' },
                                                        'FAILED': { text: '실패', color: '#dc2626', bg: '#fef2f2' },
                                                        '실패': { text: '실패', color: '#dc2626', bg: '#fef2f2' },
                                                    };
                                                    const s = statusMap[status] || { text: status || '-', color: '#6b7280', bg: '#f9fafb' };
                                                    const dateStr = data.created_at || data.request_date || data.date;
                                                    const d = dateStr ? new Date(dateStr) : null;
                                                    const dateFormatted = d
                                                        ? `${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
                                                        : '-';
                                                    return (
                                                        <div key={data.id || i} style={{
                                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                                            padding: isMobile ? "12px 14px" : "14px 20px",
                                                            background: "#fff", borderRadius: "10px",
                                                            border: "1px solid #e2e8f0",
                                                            borderLeft: `3px solid ${s.color}`
                                                        }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "10px" : "16px" }}>
                                                                <div style={{
                                                                    width: isMobile ? "32px" : "36px", height: isMobile ? "32px" : "36px",
                                                                    borderRadius: "8px", background: s.bg,
                                                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                                                }}>
                                                                    <ArrowUpCircle size={isMobile ? 16 : 18} style={{ color: s.color }} />
                                                                </div>
                                                                <div>
                                                                    <p style={{ margin: 0, fontSize: isMobile ? "14px" : "15px", fontWeight: "700", color: "#1e293b" }}>
                                                                        -{changeNum(Number(data.final_amount || data.amount || 0))}원
                                                                    </p>
                                                                    <p style={{ margin: "2px 0 0 0", fontSize: isMobile ? "11px" : "12px", color: "#94a3b8" }}>
                                                                        {dateFormatted}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "8px" : "12px" }}>
                                                                {data.balance_after != null && (
                                                                    <span style={{ fontSize: isMobile ? "11px" : "12px", color: "#64748b" }}>
                                                                        잔액 {changeNum(Number(data.balance_after))}원
                                                                    </span>
                                                                )}
                                                                <span style={{
                                                                    fontSize: isMobile ? "11px" : "12px", fontWeight: "700",
                                                                    color: "#fff", background: s.color,
                                                                    padding: "4px 12px", borderRadius: "6px"
                                                                }}>
                                                                    {s.text}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )
                                        ) : (
                                            pendingDeposits.length === 0 ? (
                                                <div style={{ padding: "60px 20px", textAlign: "center", color: "#94a3b8", fontSize: "14px" }}>
                                                    입금 내역이 없습니다.
                                                </div>
                                            ) : (
                                                pendingDeposits.map((data, i) => {
                                                    const statusMap = {
                                                        'SUCCESS': { text: '완료', color: '#059669', bg: '#f0fdf4' },
                                                        'PENDING': { text: '대기', color: '#d97706', bg: '#fffbeb' },
                                                        'FAILED': { text: '실패', color: '#dc2626', bg: '#fef2f2' },
                                                    };
                                                    const s = statusMap[data.status] || { text: data.status || '-', color: '#6b7280', bg: '#f9fafb' };
                                                    const d = data.created_at ? new Date(data.created_at) : null;
                                                    const dateFormatted = d
                                                        ? `${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
                                                        : '-';
                                                    return (
                                                        <div key={data.id || i} style={{
                                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                                            padding: isMobile ? "12px 14px" : "14px 20px",
                                                            background: "#fff", borderRadius: "10px",
                                                            border: "1px solid #e2e8f0",
                                                            borderLeft: `3px solid ${s.color}`
                                                        }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "10px" : "16px" }}>
                                                                <div style={{
                                                                    width: isMobile ? "32px" : "36px", height: isMobile ? "32px" : "36px",
                                                                    borderRadius: "8px", background: s.bg,
                                                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                                                }}>
                                                                    <ArrowDownCircle size={isMobile ? 16 : 18} style={{ color: s.color }} />
                                                                </div>
                                                                <div>
                                                                    <p style={{ margin: 0, fontSize: isMobile ? "14px" : "15px", fontWeight: "700", color: "#1e293b" }}>
                                                                        {changeNum(Number(data.amount || 0))}원
                                                                    </p>
                                                                    <p style={{ margin: "2px 0 0 0", fontSize: isMobile ? "11px" : "12px", color: "#94a3b8" }}>
                                                                        {dateFormatted}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "8px" : "12px" }}>
                                                                {data.balance_after != null && (
                                                                    <span style={{ fontSize: isMobile ? "11px" : "12px", color: "#64748b" }}>
                                                                        잔액 {changeNum(Number(data.balance_after))}원
                                                                    </span>
                                                                )}
                                                                <span style={{
                                                                    fontSize: isMobile ? "11px" : "12px", fontWeight: "700",
                                                                    color: "#fff", background: s.color,
                                                                    padding: "4px 12px", borderRadius: "6px"
                                                                }}>
                                                                    {s.text}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            </div>
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @media (max-width: 767px) {
                    .content-wrap {
                        padding: 10px !important;
                        width: 100% !important;
                        max-width: 100vw !important;
                        box-sizing: border-box !important;
                    }
                    
                    .content-container {
                        width: 100% !important;
                        max-width: 100vw !important;
                        box-sizing: border-box !important;
                    }
                    
                    .inner-wrap {
                        width: 100% !important;
                        max-width: 100vw !important;
                        box-sizing: border-box !important;
                    }
                    
                    .agent_panel {
                        flex-direction: column !important;
                        width: 100% !important;
                        gap: 4px !important;
                    }
                    
                    .agent_panel-1,
                    .agent_panel-2 {
                        width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    
                    .intro-y.box {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                        width: 100% !important;
                        max-width: 100vw !important;
                        box-sizing: border-box !important;
                    }
                    
                    /* 테이블 컨테이너 */
                    .tabulator {
                        width: 100% !important;
                        max-width: 100vw !important;
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    .tabulator-tableHolder {
                        width: 100% !important;
                        max-width: 100vw !important;
                        overflow-x: auto !important;
                    }
                    
                    .tabulator-header {
                        min-width: 100% !important;
                    }
                    
                    .tabulator-table {
                        min-width: 100% !important;
                    }
                    
                    .main-title-wrap {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 10px !important;
                        width: 100% !important;
                    }
                    
                    .content-item {
                        width: 100% !important;
                        max-width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    
                    /* 테이블 컬럼 width 조정 */
                    .table-report th,
                    .table-report td {
                        min-width: auto !important;
                    }
                    
                    /* 테이블 컨테이너 */
                    .tabulator {
                        width: 100% !important;
                        max-width: 100vw !important;
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    .tabulator-tableHolder {
                        width: 100% !important;
                        max-width: 100vw !important;
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    /* 테이블이 화면을 벗어나지 않도록 */
                    #tabulator2 {
                        width: 100% !important;
                        max-width: 100vw !important;
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    /* 카드 내부 텍스트 */
                    .calc-title {
                        font-size: 12px !important;
                    }
                    
                    .calc-item money {
                        font-size: 18px !important;
                    }
                    
                    .calc-item span {
                        font-size: 14px !important;
                    }
                }
            `}</style>
        </>
    )
}

// 출금 모달 컴포넌트
function WithdrawModal({
    currentBalance,
    withdrawable,
    onClose,
    onWithdraw,
    parseKRW,
    withdrawalFee = 0, // 출금 수수료 (건당 정액)
}) {
    const [input, setInput] = useState("");
    const amount = useMemo(() => parseKRW(input), [input, parseKRW]);

    // 수수료 계산
    const fee = useMemo(() => {
        return Number(withdrawalFee) || 0;
    }, [withdrawalFee]);

    // 실제 출금 금액 (출금 요청 금액 - 수수료)
    const actualAmount = useMemo(() => {
        return Math.max(0, amount - fee);
    }, [amount, fee]);

    // 출금 후 잔액
    const remain = useMemo(
        () => Math.max(0, withdrawable - amount),
        [withdrawable, amount]
    );

    const over = amount > withdrawable;
    const invalid = amount <= 0 || over;

    const handleAll = () => setInput(changeNum(withdrawable));
    const handleChange = (e) => setInput(changeNum(parseKRW(e.target.value)));

    const handleWithdraw = useCallback(async () => {
        if (invalid) return;
        try {
            await onWithdraw?.(amount);
        } catch (e) {
            console.error(e);
        }
    }, [amount, invalid, onWithdraw]);

    // ESC로 닫기
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div 
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                padding: "20px"
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: "12px",
                    width: "100%",
                    maxWidth: "400px",
                    maxHeight: "90vh",
                    overflow: "auto",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.2)"
                }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="withdraw-title"
            >
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    borderBottom: "1px solid #e2e8f0"
                }}>
                    <h2 id="withdraw-title" style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#1e293b" }}>
                        출금 요청
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label="닫기"
                        style={{
                            background: "none",
                            border: "none",
                            fontSize: "24px",
                            color: "#64748b",
                            cursor: "pointer",
                            padding: "0",
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        ✕
                    </button>
                </div>

                <div style={{ padding: "20px" }}>
                    <ModalRow label="현재 잔액">
                        <strong style={{ fontSize: "16px", color: "#059669" }}>
                            {changeNum(currentBalance)}원
                        </strong>
                    </ModalRow>

                    <ModalRow label="출금 가능 금액">
                        <strong style={{ fontSize: "16px", color: "#2563eb" }}>
                            {changeNum(withdrawable)}원
                        </strong>
                    </ModalRow>

                    <ModalRow label="출금 금액">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="0"
                                value={input}
                                onChange={handleChange}
                                style={{
                                    width: "150px",
                                    padding: "8px 12px",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "6px",
                                    fontSize: "14px"
                                }}
                            />
                            <span style={{ fontSize: "14px", color: "#64748b" }}>원</span>
                            <button
                                onClick={handleAll}
                                style={{
                                    padding: "6px 12px",
                                    background: "#f1f5f9",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    color: "#475569",
                                    fontWeight: "500"
                                }}
                            >
                                전액
                            </button>
                        </div>
                    </ModalRow>

                    <ModalRow label="출금 수수료">
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
                            <strong style={{ fontSize: "15px", color: "#dc2626" }}>
                                {changeNum(fee)}원
                            </strong>
                            {fee > 0 && (
                                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                                    건당 {changeNum(fee)}원
                                </span>
                            )}
                        </div>
                    </ModalRow>

                    {amount > 0 && (
                        <ModalRow label="실제 출금 금액">
                            <strong style={{ fontSize: "16px", color: "#2563eb" }}>
                                {changeNum(actualAmount)}원
                            </strong>
                        </ModalRow>
                    )}

                    <ModalRowLast label="출금 후 잔액">
                        {over ? (
                            <p style={{ color: "#dc2626", fontSize: "13px", margin: 0 }}>
                                출금 가능 금액을 초과했습니다.
                            </p>
                        ) : (
                            <strong style={{ fontSize: "16px", color: "#059669" }}>
                                {changeNum(remain)}원
                            </strong>
                        )}
                    </ModalRowLast>
                </div>

                <div style={{
                    padding: "20px",
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    gap: "10px"
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            flex: 1,
                            padding: "12px",
                            background: "#f1f5f9",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#475569",
                            cursor: "pointer"
                        }}
                    >
                        취소
                    </button>
                    <button
                        onClick={handleWithdraw}
                        disabled={invalid}
                        style={{
                            flex: 1,
                            padding: "12px",
                            background: invalid ? "#e2e8f0" : "#16639C",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: invalid ? "#94a3b8" : "#fff",
                            cursor: invalid ? "not-allowed" : "pointer",
                            transition: "all 0.2s"
                        }}
                    >
                        출금 요청
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModalRow({ label, children }) {
    return (
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 0",
            borderBottom: "1px solid #f1f5f9"
        }}>
            <div style={{ fontSize: "14px", color: "#64748b", fontWeight: "500" }}>{label}</div>
            <div>{children}</div>
        </div>
    );
}

function ModalRowLast({ label, children }) {
    return (
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 0",
            borderTop: "1px solid #e2e8f0",
            marginTop: "12px",
            paddingTop: "16px"
        }}>
            <div style={{ fontSize: "14px", color: "#64748b", fontWeight: "500" }}>{label}</div>
            <div>{children}</div>
        </div>
    );
}

export default AgentDashboard;
